import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AdminUserListModel } from '../../shared/models';
import { Column } from 'devextreme/ui/data_grid';
import { trigger } from 'devextreme/events';
import { Router } from '@angular/router';
import { DxDataGridComponent } from 'devextreme-angular';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { AnalyticsFileFormat, AnalyticsReportType, AnalyticsService } from 'app/shared/analytics';

@Component({
  selector: 'sp-admin-user-list',
  templateUrl: './admin-user-list.component.html',
  styleUrls: ['./admin-user-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminUserListComponent implements OnChanges {

  @Input() adminUsers: AdminUserListModel[];
  @Input() userFilterSettings: string;
  @Output() filterChanged: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('dxAdminUsersDataGrid', { static: false }) dxAdminUsersDataGrid: DxDataGridComponent;
  dataGridSource: any;
  selectedActiveFilter = 'active';
  exportInProgress = false;
  activeFilterItems = [
    { value: 'active', text: 'Active' },
    { value: 'inactive', text: 'Inactive' },
    { value: 'all', text: 'All' }
  ];

  constructor(private router: Router, private analyticsService: AnalyticsService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.adminUsers && this.adminUsers) {
      this.updateDataGridSource(this.selectedActiveFilter);
    }
  }

  activeDropdownChanged(e: any) {
    this.selectedActiveFilter = e.value;
    this.updateDataGridSource(e.value);
  }

  updateDataGridSource(selectedValue: string) {
    if (selectedValue === 'active') {
      this.dataGridSource = this.adminUsers?.filter(x => x.isActive == true);
    } else if (selectedValue === 'inactive') {
      this.dataGridSource = this.adminUsers?.filter(x => x.isActive == false);
    } else {
      this.dataGridSource = this.adminUsers;
    }
  }

  onSearchValueChanged(e) {
    if (e.fullName == 'searchPanel.text' && this.userFilterSettings !== e.value) {
      this.filterChanged.emit(e.value);
    }
  }

  dxGridRowPrepared(e) {
    if (e.rowType === 'data') {
      e.rowElement.style.height = '40px';
    }
  }

  dxCalculateUserNameSortValue(rowData: AdminUserListModel) {
    return rowData.firstName;
  }

  /** Display value is name only; include email in value so search panel can find by email. */
  dxCalculateUserNameCellValue(e: AdminUserListModel) {
    const { firstName, lastName, email } = e;
    const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return name ? `${name} ${email ?? ''}`.trim() : (email ?? '');
  }

  dxCalculateUserNameFilter(this: Column, filterValue: string, selectedFilterOperation: string, target: string) {
    if (target === 'search') {
      return [this.calculateCellValue, 'contains', filterValue];
    }
    // eslint-disable-next-line prefer-spread, prefer-rest-params
    return this.defaultCalculateFilterExpression.apply(this, arguments);
  }

  showActionMenu(e) {
    // This allows us to either left or right click to show the menu.
    const newEvent = Object.assign({}, e.event, { type: 'dxcontextmenu' });
    e.event.stopPropagation();
    trigger(e.element, newEvent);
  }

  dxOnContextMenuPreparing(e) {

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // Only show the Context Menu if the Menu Button is clicked!
    if (e.row.rowType === 'data' && e.column.cellTemplate === 'userId') {

      const userId = e.row.data.userId;

      e.items = [
        {
          text: 'Edit',
          onItemClick: function () {
            that.router.navigate(['/useraccount/admin/users/', userId]);
          }
        }
      ];
    }
  }

  exportExcel() {
    if (this.exportInProgress) {
      return;
    }
    this.exportInProgress = true;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Users');
    const currentDate = new Date().toISOString().split('T')[0];
    const gridInstance = this.dxAdminUsersDataGrid.instance;

    exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell.rowType === 'data') {
          if (gridCell.column.caption === 'User Name') {
            const d = gridCell.data;
            excelCell.value = d ? `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() : '';
          } else if (gridCell.column.dataField === 'isActive') {
            excelCell.value = gridCell.data?.isActive === true ? 'Active' : 'Inactive';
          }
        }
      },
    })
      .then(() => {
        return workbook.xlsx.writeBuffer().then((buffer) => {
          const fileName = `AdminUsers-${currentDate}.xlsx`;
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
          this.analyticsService.reportExportedEvent(AnalyticsFileFormat.Excel, AnalyticsReportType.AdminUsers);
        });
      })
      .finally(() => {
        this.exportInProgress = false;
      });
  }
}
